import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MapPin, TrendingUp, ArrowRight, Zap } from 'lucide-react';
import DashboardLayout from '@/components/DashboardLayout';

// Investor Cashback Tiers (Based on Investment Amount)
// Base: ₹0, Silver: ₹10L, Gold: ₹30L, Platinum: ₹90L, Diamond: ₹2.7Cr
const investorTiers = [
  { name: 'Base', threshold: 0, cashback: 0.5, icon: '🌱' },
  { name: 'Silver', threshold: 1000000, cashback: 1.0, icon: '🥈' },      // ₹10 Lakh
  { name: 'Gold', threshold: 3000000, cashback: 1.5, icon: '🥇' },        // ₹30 Lakh
  { name: 'Platinum', threshold: 9000000, cashback: 2.0, icon: '💎' },    // ₹90 Lakh
  { name: 'Diamond', threshold: 27000000, cashback: 3.0, icon: '👑' },    // ₹2.7 Crore
];

export default function EstateDetailNew({ user }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [investmentAmount, setInvestmentAmount] = useState(1000000);
  
  const property = {
    id: id || '1',
    name: 'Premium Skyline Tower',
    location: 'Sector 48, Golf Course Road, Gurgaon',
    developer: 'Emaar India',
    unitValue: 48000000,
    minInvestment: 500000,
    maxInvestment: 10000000,
    eoiFee: 25000,
    completionDate: 'Q4 2027',
    unitSize: '3200 sqft',
    configuration: '4 BHK',
    image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200',
    historicalReturns: [
      { year: 2023, return: 11.2 },
      { year: 2024, return: 12.8 },
      { year: 2025, return: 14.3 }
    ],
    areaAverageReturn: 13.1,
    sector: 'Sector 48'
  };

  const ownershipPercentage = (investmentAmount / property.unitValue) * 100;
  
  const getCurrentTier = (amount) => {
    for (let i = investorTiers.length - 1; i >= 0; i--) {
      if (amount >= investorTiers[i].threshold) return investorTiers[i];
    }
    return investorTiers[0];
  };

  const currentTier = getCurrentTier(investmentAmount);
  const cashbackAmount = (investmentAmount * currentTier.cashback) / 100;

  const formatINR = (val) => {
    if (val >= 10000000) return `₹${(val/10000000).toFixed(2)} Cr`;
    if (val >= 100000) return `₹${(val/100000).toFixed(2)} L`;
    return `₹${val.toLocaleString('en-IN')}`;
  };

  const handleBookInvestment = () => {
    navigate(`/invest/${property.id}?amount=${investmentAmount}`);
  };

  return (
    <div className="min-h-screen bg-black">
      <DashboardLayout user={user}>
        <div className="p-6 md:p-10">
          <div className="mb-10">
            <button onClick={() => navigate('/investor/estates')} className="text-sm text-gray-500 hover:text-white mb-4">← Back</button>
            <h1 className="text-3xl font-light text-white mb-2">{property.name}</h1>
            <div className="flex items-center gap-2 text-gray-400">
              <MapPin className="w-4 h-4" />
              <p className="text-sm">{property.location}</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div className="h-[400px] border border-white/10">
                <img src={property.image} alt={property.name} className="w-full h-full object-cover" />
              </div>

              <div className="border border-white/10 p-6">
                <h2 className="text-lg font-medium text-white mb-4">Location & Returns</h2>
                <div className="bg-white/5 border border-white/10 h-[300px] mb-6 flex items-center justify-center relative">
                  <MapPin className="w-16 h-16 text-[#4ADE80]/20" />
                  <p className="text-sm text-gray-500">Google Maps</p>
                  <div className="absolute top-4 right-4 bg-[#4ADE80]/20 border border-[#4ADE80]/50 px-4 py-2">
                    <p className="text-xs text-[#4ADE80]">Sector 48</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs text-gray-600 uppercase mb-3">Area Avg Return</p>
                    <p className="font-mono text-3xl text-[#4ADE80]">{property.areaAverageReturn}%</p>
                    <p className="text-xs text-gray-600 mt-1">Last 3 years</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 uppercase mb-3">History</p>
                    {property.historicalReturns.map((y) => (
                      <div key={y.year} className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">{y.year}</span>
                        <span className="font-mono text-white">{y.return}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="border border-white/10 p-6">
                <h2 className="text-lg font-medium text-white mb-6">Details</h2>
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs text-gray-600 mb-2">Developer</p>
                    <p className="text-sm text-white">{property.developer}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-2">Size</p>
                    <p className="text-sm text-white">{property.unitSize}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600 mb-2">Config</p>
                    <p className="text-sm text-white">{property.configuration}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="border border-white/10 sticky top-6">
                <div className="bg-white/5 px-6 py-4 border-b border-white/10">
                  <h2 className="text-sm font-medium text-white">Book Investment</h2>
                </div>

                <div className="p-6">
                  <div className="mb-6">
                    <div className="flex justify-between mb-3">
                      <span className="text-sm text-gray-500">Amount</span>
                      <span className="font-mono text-xl text-white">{formatINR(investmentAmount)}</span>
                    </div>
                    <input type="range" min={property.minInvestment} max={property.maxInvestment} step={100000} value={investmentAmount} onChange={(e) => setInvestmentAmount(parseInt(e.target.value))} className="w-full h-px bg-white/10 slider-thumb" />
                    <div className="flex justify-between text-xs text-gray-600 mt-2">
                      <span>{formatINR(property.minInvestment)}</span>
                      <span>{formatINR(property.maxInvestment)}</span>
                    </div>
                  </div>

                  <div className="bg-[#4ADE80]/10 border border-[#4ADE80]/30 p-4 mb-4">
                    <p className="text-xs text-gray-400 mb-1">Share</p>
                    <p className="font-mono text-3xl text-[#4ADE80]">{ownershipPercentage.toFixed(2)}%</p>
                  </div>

                  <div className="bg-purple-500/10 border border-purple-500/30 p-4 mb-6">
                    <div className="flex justify-between mb-2">
                      <p className="text-xs text-gray-400">Cashback</p>
                      <span className="text-xs">{currentTier.icon}</span>
                    </div>
                    <p className="font-mono text-2xl text-white">{formatINR(cashbackAmount)}</p>
                  </div>

                  <button onClick={handleBookInvestment} className="w-full bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold py-4 flex items-center justify-center gap-2">
                    Book Investment
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  
                  <p className="text-xs text-gray-600 text-center mt-4">₹25K refundable EOI</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    </div>
  );
}
